import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { PocTier, TenantTierService } from 'src/app/Services/tenant-tier.service';

interface TierMeta {
  label: string;
  nudge: string;
  tooltip: string;
  /**
   * Optional router target for the nudge text. When set, the nudge renders as
   * a link; otherwise it renders as a plain span. Tier 1 points to the Tier 2
   * upload page so users can act on the upgrade prompt in one click.
   */
  nudgeLink?: string;
}

/**
 * Renders a "PoC · Tier N" pill plus a one-line nudge to the next tier in the
 * blue header strip, beside the user-info dropdown. Hidden entirely when the
 * tenant is not a PoC (the backend returns `tier: null` — see
 * {@link TenantTierService}).
 */
@Component({
  selector: 'app-poc-tier-bar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './poc-tier-bar.component.html',
  styleUrls: ['./poc-tier-bar.component.scss'],
})
export class PocTierBarComponent implements OnInit, OnDestroy {
  // Copy lives in the frontend (per design decision) — translation can come later
  // once we get the keys into the i18n catalogues.
  private static readonly TIER_META: Record<Exclude<PocTier, null>, TierMeta> = {
    tier_1: {
      label: 'PoC · Tier 1',
      nudge: 'Upgrade to Tier 2 to see your own classified data',
      nudgeLink: '/tier2-upload',
      tooltip:
        'Tier 1 (current): exploring Spendkey with generic placeholder data. ' +
        'Tier 2: the same experience with your own data, classified by Spendkey. ' +
        'Tier 3: our standard paid offering.',
    },
    tier_2: {
      label: 'PoC · Tier 2',
      nudge: 'Talk to us about Tier 3 to unlock the full platform',
      tooltip:
        'Tier 1: generic placeholder data. ' +
        'Tier 2 (current): your own data classified by Spendkey. ' +
        'Tier 3: our standard paid offering — full platform, dedicated support.',
    },
  };

  public meta: TierMeta | null = null;

  private readonly tierService = inject(TenantTierService);
  private subscription: Subscription | null = null;

  ngOnInit(): void {
    this.tierService.startPolling();
    this.subscription = this.tierService.tier$.subscribe((tier) => {
      this.meta = tier ? PocTierBarComponent.TIER_META[tier] : null;
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
